// per docs/ui-shape-grafana-authoring.md §4 — who actually reaches a dashboard
// (caiet UC26-02, UC61-01/02/03, decision D5).
//
// The checkbox list answers "which grants are written on this dashboard". That
// is not the same question as "who will see it": a Specific Observer is an
// Observer, so a grant addressed to OBSERVER already carries every OBSERVER_*
// role, including the ones minted in MPass after this shipped. An administrator
// who cannot see that grants it a second time and then cannot revoke it.
//
// The membership rule stays in @simsm/shared-types; this is its projection over
// the roles SIMSM defines today, and the inverse of grantKindFor in
// features/dashboards/role-scope.ts.
import { roleSatisfiesGrant } from '@simsm/shared-types';
import { useObserverRoles } from './use-observer-roles';

export type ReachKind =
  // role_scope names this role
  | 'direct'
  // role_scope names a grant this role satisfies without being named
  | 'inherited';

export interface RoleReach {
  roleCode: string;
  kind: ReachKind;
  // The grant an inherited reach comes through; null for a direct one.
  via: string | null;
}

// An empty role_scope is "not shared" (docs/shape-ops-dashboard-sharing.md §2):
// it reaches no observer role, only the System Administrator.
export function resolveDashboardReach(
  roleScope: readonly string[],
  roleCodes: readonly string[],
): RoleReach[] {
  return roleCodes.flatMap((roleCode): RoleReach[] => {
    if (roleScope.includes(roleCode)) {
      return [{ roleCode, kind: 'direct', via: null }];
    }
    const via = roleScope.find((grant) => roleSatisfiesGrant(roleCode, grant));
    return via ? [{ roleCode, kind: 'inherited', via }] : [];
  });
}

// A grant on a role SIMSM no longer defines still decides access, so it must
// stay visible: dropping it from the panel would read as a dashboard nobody
// reaches while the row keeps letting that role in.
export function undefinedGrants(
  roleScope: readonly string[],
  roleCodes: readonly string[],
): string[] {
  return roleScope.filter((grant) => !roleCodes.includes(grant));
}

export function useDashboardReach(): (roleScope: readonly string[]) => RoleReach[] {
  const roles = useObserverRoles();
  const roleCodes = (roles.data ?? []).map((role) => role.roleCode);
  return (roleScope: readonly string[]): RoleReach[] => resolveDashboardReach(roleScope, roleCodes);
}
